/**
 * Given a template string containing a trailing number and a count,
 * generates an array of `count` names by incrementing the number.
 *
 * Zero-padding is preserved: "Pen 01" → ["Pen 01","Pen 02",…]
 * Non-padded numbers work too: "Cage 3" → ["Cage 3","Cage 4",…]
 * If no trailing number is found the template is used as-is for the
 * first item; subsequent items get " 02", " 03", … appended.
 *
 * @example
 * generateCageNames("Pen 01", 3) // ["Pen 01", "Pen 02", "Pen 03"]
 * generateCageNames("Cage 3",  3) // ["Cage 3",  "Cage 4",  "Cage 5"]
 * generateCageNames("Alpha",   3) // ["Alpha",   "Alpha 02","Alpha 03"]
 */
export function generateCageNames(template: string, count: number): string[] {
    if (count <= 0) return [];

    // Match the LAST digit sequence in the string so "Pen 01" matches "01", not earlier digits.
    const match = template.match(/^([\s\S]*?)(\d+)(\D*)$/);
    if (!match) {
        return Array.from({ length: count }, (_, i) =>
            i === 0 ? template : `${template} ${String(i + 1).padStart(2, '0')}`
        );
    }

    const [, prefix, digits, suffix] = match;
    const start = Number.parseInt(digits, 10);
    const pad = digits.length;

    return Array.from(
        { length: count },
        (_, i) => `${prefix}${String(start + i).padStart(pad, '0')}${suffix}`
    );
}
